import {
  AfterViewInit,
  contentChild,
  Directive,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { NzThAddOnComponent, NzTheadComponent } from 'ng-zorro-antd/table';
import { Subscription } from 'rxjs';
import { ColumnLayoutRegistry } from '../services/ColumnLayoutRegistry';
import { UserSettingsService } from '../services/UserSettingsService';
import {
  COLUMN_HIDDEN_CLASS,
  COLUMN_LAYOUT_EVENT,
  COLUMN_MIN_PX,
  COLUMN_USER_WIDTH_ATTR,
  columnLayoutKey,
  IColumnLayout,
  IColumnPickerItem,
  measureColumnAutosizeWidth,
  measureColumnHeaderContentWidth,
  measureColumnHeaderMinWidth,
  mergeColumnLayout,
  isPositionalColumnKey,
  parseColumnLayout,
  placeColumnKey,
  sanitizeHidden,
  fillColumnWidth,
} from '../utils/column-layout';

const HANDLE_CLASS = 'col-layout-handle';
const KEY_ATTR = 'data-col-key';
const DEFAULT_WIDTH_ATTR = 'data-default-width';
const DEFAULT_INDEX_ATTR = 'data-default-index';
const DROP_BEFORE_CLASS = 'col-layout-drop-before';
const DROP_AFTER_CLASS = 'col-layout-drop-after';
const MENU_CLASS = 'col-layout-menu';
const DRAG_THRESHOLD_PX = 6;
const HEADER_CONTROL_SELECTOR =
  '.ant-table-column-sorter, .ant-table-filter-trigger, nz-filter-trigger, button, a, input';

@Directive({
  selector: 'nz-table[appColumnLayout]',
  standalone: false
})
export class ColumnLayoutDirective implements AfterViewInit, OnDestroy {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly userSettings = inject(UserSettingsService);
  private readonly registry = inject(ColumnLayoutRegistry);
  private readonly zone = inject(NgZone);
  private readonly thead = contentChild(NzTheadComponent);

  private tableId = '';
  private settingKey = '';
  private observer: MutationObserver | null = null;
  private setupQueued = false;
  private destroyed = false;
  private applying = false;
  private resizing = false;
  private reordering = false;
  private didInitialNotify = false;
  private headerPointerUnlisten: (() => void) | null = null;
  private menuUnlisten: (() => void) | null = null;
  private menuEl: HTMLElement | null = null;
  private allowHeaderClick = false;
  private hiddenKeys = new Set<string>();
  private readonly defaultIndexByKey = new Map<string, number>();
  private readonly api = {
    reset: () => this.reset(),
    listColumns: () => this.listColumns(),
    setColumnVisible: (key: string, visible: boolean) => this.setColumnVisible(key, visible),
  };
  private loadedSub: Subscription | null = null;

  ngAfterViewInit(): void {
    this.tableId = this.el.nativeElement.getAttribute('id') || '';
    if (!this.tableId) {
      return;
    }
    this.settingKey = columnLayoutKey(this.tableId);
    this.registry.register(this.tableId, this.api);
    this.loadedSub = this.userSettings.loaded$.subscribe(ready => {
      if (!ready) {
        return;
      }
      this.applyLayout(false, false);
      this.scheduleWidthRemeasure();
    });
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.scheduleSetup());
      this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
    });
    this.scheduleSetup();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.registry.unregister(this.tableId, this.api);
    this.loadedSub?.unsubscribe();
    this.observer?.disconnect();
    this.headerPointerUnlisten?.();
    this.closeColumnMenu();
    this.menuUnlisten?.();
  }

  reset(): void {
    if (!this.settingKey) {
      return;
    }
    this.userSettings.delete(this.settingKey);
    this.hiddenKeys.clear();
    this.applyDefaultOrder();
    this.clearUserWidths();
    this.applyVisibility([]);
    this.notify();
    this.reapplyAfterRender();
  }

  private scheduleSetup(): void {
    if (this.destroyed || this.applying || this.resizing || this.reordering || this.setupQueued) {
      return;
    }
    this.setupQueued = true;
    queueMicrotask(() => {
      this.setupQueued = false;
      if (this.destroyed || this.applying || this.resizing || this.reordering) {
        return;
      }
      this.setup();
    });
  }

  private setup(): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    this.observer?.disconnect();
    this.stampKeys(headerRow);
    this.applyLayout(false, false);
    this.attachResizeHandles();
    this.attachReorder(headerRow);
    this.attachColumnMenu(headerRow);
    this.scheduleHeaderTooltips();
    this.observer?.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  private scheduleWidthRemeasure(): void {
    requestAnimationFrame(() => {
      if (this.destroyed) {
        return;
      }
      this.notify();
    });
  }

  private isGroupedHeader(): boolean {
    return this.el.nativeElement.querySelectorAll('.ant-table-thead tr').length > 1;
  }

  private leafHeaderRow(): HTMLTableRowElement | null {
    const rows = Array.from(this.el.nativeElement.querySelectorAll<HTMLTableRowElement>('.ant-table-thead tr'));
    const matching = rows.filter(row => row.children.length > 0);
    const visible = matching.filter(row => row.getBoundingClientRect().height > 0);
    const pool = visible.length ? visible : matching;
    if (!pool.length) {
      return null;
    }
    return pool[pool.length - 1];
  }

  private stampKeys(headerRow: HTMLTableRowElement): void {
    const count = headerRow.children.length;
    const captureTemplateOrder = this.defaultIndexByKey.size === 0;
    this.el.nativeElement.querySelectorAll<HTMLTableRowElement>('.ant-table-thead tr').forEach(row => {
      if (row.children.length !== count) {
        return;
      }
      Array.from(row.children).forEach((cell, index) => {
        const th = cell as HTMLElement;
        const key = this.columnKey(th);
        if (!key) {
          const stamped = th.getAttribute(KEY_ATTR);
          if (stamped && isPositionalColumnKey(stamped)) {
            th.removeAttribute(KEY_ATTR);
          }
          return;
        }
        th.setAttribute(KEY_ATTR, key);
        if (!this.defaultIndexByKey.has(key)) {
          this.defaultIndexByKey.set(key, captureTemplateOrder ? index : this.nextDefaultIndex());
        }
        th.setAttribute(DEFAULT_INDEX_ATTR, String(this.defaultIndexByKey.get(key)));
        const declared = parseFloat(th.getAttribute('nzwidth') || '');
        const headerContent = measureColumnHeaderContentWidth(th);
        const next = Math.max(
          COLUMN_MIN_PX,
          Number.isFinite(declared) && declared > 0 ? declared : 0,
          headerContent,
        );
        const prev = parseFloat(th.getAttribute(DEFAULT_WIDTH_ATTR) || '');
        // Never store the live box. A fixed table on the first paint is already squeezed,
        // and that value was then reused as the column width until a full reload.
        if (!Number.isFinite(prev) || next > prev) {
          th.setAttribute(DEFAULT_WIDTH_ATTR, String(Math.round(next)));
        }
      });
    });
    this.stampBodyKeys();
  }

  private stampBodyKeys(): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    const keyByDefaultIndex: string[] = [];
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const index = parseInt(th.getAttribute(DEFAULT_INDEX_ATTR) || '', 10);
      const key = th.getAttribute(KEY_ATTR) || '';
      if (Number.isInteger(index) && index >= 0 && key) {
        keyByDefaultIndex[index] = key;
      }
    });
    this.dataRows().forEach(row => {
      if (row.closest('.ant-table-thead')) {
        return;
      }
      const cells = Array.from(row.children) as HTMLElement[];
      cells.forEach((cell, index) => {
        if (cell.getAttribute(KEY_ATTR)) {
          return;
        }
        const key = keyByDefaultIndex[index];
        // Live index is the template index only while this row is still in template order.
        // A key already owned by another cell means the row was reordered; guessing would
        // stick a heading over the wrong value.
        const taken = !!key && cells.some(other => other !== cell && other.getAttribute(KEY_ATTR) === key);
        if (key && !taken) {
          cell.setAttribute(KEY_ATTR, key);
        }
      });
    });
  }

  private nextDefaultIndex(): number {
    let max = -1;
    this.defaultIndexByKey.forEach(index => {
      if (index > max) {
        max = index;
      }
    });
    return max + 1;
  }

  private columnKey(th: HTMLElement): string | null {
    const declared = this.declaredColumnKey(th);
    if (declared && !isPositionalColumnKey(declared)) {
      return declared;
    }
    const stamped = th.getAttribute(KEY_ATTR);
    if (stamped && !isPositionalColumnKey(stamped)) {
      return stamped;
    }
    return null;
  }

  private declaredColumnKey(th: HTMLElement): string | null {
    const fromAttr = th.getAttribute('nzcolumnkey') || th.getAttribute('nzColumnKey');
    if (fromAttr) {
      return fromAttr;
    }
    const addon = this.thead()?.listOfNzThAddOnComponent?.find(item => {
      const host = (item as unknown as { el?: { nativeElement?: HTMLElement } }).el?.nativeElement;
      return host === th;
    });
    if (addon?.nzColumnKey == null || addon.nzColumnKey === '') {
      return null;
    }
    return String(addon.nzColumnKey);
  }

  private isFixed(th: HTMLElement): boolean {
    return th.hasAttribute('nzleft')
      || th.hasAttribute('nzright')
      || th.hasAttribute('nzLeft')
      || th.hasAttribute('nzRight')
      || th.classList.contains('ant-table-cell-fix-left')
      || th.classList.contains('ant-table-cell-fix-right');
  }

  private lockedKeys(headerRow: HTMLTableRowElement): string[] {
    return Array.from(headerRow.children)
      .filter(cell => this.isFixed(cell as HTMLElement))
      .map(cell => (cell as HTMLElement).getAttribute(KEY_ATTR))
      .filter((key): key is string => !!key);
  }

  private scheduleHeaderTooltips(): void {
    requestAnimationFrame(() => {
      if (this.destroyed) {
        return;
      }
      this.syncHeaderTooltips();
    });
  }

  private syncHeaderTooltips(): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      if (this.isFixed(th) || th.classList.contains(COLUMN_HIDDEN_CLASS)) {
        return;
      }
      const label = th.querySelector('.ant-table-column-title') as HTMLElement | null;
      const target = label || th;
      const text = ((label?.textContent || th.innerText) || '').replace(/\s+/g, ' ').trim();
      if (text && target.scrollWidth > target.clientWidth + 1) {
        target.setAttribute('title', text);
      } else {
        target.removeAttribute('title');
      }
    });
  }

  private columnLabel(th: HTMLElement): string {
    const title = th.querySelector('.ant-table-column-title');
    const text = (title?.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) {
      return text;
    }
    if (th.classList.contains('ant-table-selection-column')) {
      return 'Auswahl';
    }
    if (this.isFixed(th)) {
      return 'Aktionen';
    }
    return 'Spalte';
  }

  private listColumns(): IColumnPickerItem[] {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return [];
    }
    this.stampKeys(headerRow);
    return Array.from(headerRow.children).map(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR) || '';
      const locked = this.isFixed(th);
      return {
        key,
        label: this.columnLabel(th),
        visible: locked || !this.hiddenKeys.has(key),
        locked,
      };
    }).filter(item => !!item.key);
  }

  private setColumnVisible(key: string, visible: boolean): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow || !key) {
      return;
    }
    const keys = this.currentKeys(headerRow);
    const locked = new Set(this.lockedKeys(headerRow));
    if (locked.has(key)) {
      return;
    }
    const next = new Set(this.hiddenKeys);
    if (visible) {
      next.delete(key);
    } else {
      const hideable = keys.filter(item => !locked.has(item));
      const stillVisible = hideable.filter(item => item !== key && !next.has(item));
      if (!stillVisible.length) {
        return;
      }
      next.add(key);
    }
    this.hiddenKeys = new Set(sanitizeHidden(keys, [...next], [...locked]));
    this.applyVisibility([...this.hiddenKeys]);
    const pinned = this.layoutWidths();
    const pinKeys = new Set(
      Object.keys(pinned).filter(item => !this.hiddenKeys.has(item) && !locked.has(item) && pinned[item] > 0),
    );
    if (pinKeys.size) {
      this.applyWidths(pinned, pinKeys);
    }
    this.zone.run(() => this.persist());
  }

  private applyVisibility(hidden: string[]): void {
    const hide = new Set(hidden);
    this.el.nativeElement.querySelectorAll<HTMLElement>(`th[${KEY_ATTR}], td[${KEY_ATTR}]`).forEach(cell => {
      const key = cell.getAttribute(KEY_ATTR) || '';
      const shouldHide = !!key && hide.has(key) && !this.isFixed(cell);
      cell.classList.toggle(COLUMN_HIDDEN_CLASS, shouldHide);
    });
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    const keys = this.currentKeys(headerRow);
    this.el.nativeElement.querySelectorAll('table').forEach(table => {
      const cols = table.querySelectorAll<HTMLElement>('col');
      keys.forEach((key, index) => {
        if (!cols[index]) {
          return;
        }
        cols[index].classList.toggle(COLUMN_HIDDEN_CLASS, hide.has(key));
      });
    });
    this.syncColgroup(this.layoutWidths());
    this.syncTableWidth(this.layoutWidths());
  }

  private layoutWidths(): Record<string, number> {
    const headerRow = this.leafHeaderRow();
    const widths: Record<string, number> = {};
    if (!headerRow) {
      return widths;
    }
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR);
      if (!key) {
        return;
      }
      if (this.hiddenKeys.has(key) && !this.isFixed(th)) {
        widths[key] = 0;
        return;
      }
      const userWidth = parseFloat(th.getAttribute(COLUMN_USER_WIDTH_ATTR) || '');
      const declared = parseFloat(th.getAttribute('nzwidth') || '');
      const headerContent = measureColumnHeaderContentWidth(th);
      const fallback = Number.isFinite(userWidth) && userWidth > 0
        ? userWidth
        : Math.max(Number.isFinite(declared) && declared > 0 ? declared : 0, headerContent);
      widths[key] = Math.max(COLUMN_MIN_PX, Math.round(fallback));
    });
    return widths;
  }

  private currentKeys(headerRow: HTMLTableRowElement): string[] {
    return Array.from(headerRow.children)
      .map(cell => (cell as HTMLElement).getAttribute(KEY_ATTR))
      .filter((key): key is string => !!key);
  }

  private defaultWidths(headerRow: HTMLTableRowElement): Record<string, number> {
    const widths: Record<string, number> = {};
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR);
      if (!key) {
        return;
      }
      const storedDefault = parseFloat(th.getAttribute(DEFAULT_WIDTH_ATTR) || '');
      widths[key] = Math.max(
        COLUMN_MIN_PX,
        Number.isFinite(storedDefault) ? storedDefault : COLUMN_MIN_PX,
      );
    });
    return widths;
  }

  private applyLayout(useDefaults: boolean, notify: boolean): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    this.applying = true;
    try {
      this.stampKeys(headerRow);
      const keys = this.currentKeys(headerRow);
      const defaults = this.defaultWidths(headerRow);
      const stored = useDefaults ? null : parseColumnLayout(this.userSettings.get(this.settingKey));
      const layout = mergeColumnLayout(keys, stored, defaults);
      this.hiddenKeys = new Set(sanitizeHidden(keys, layout.hidden || [], this.lockedKeys(headerRow)));
      if (!this.isGroupedHeader()) {
        this.applyOrder(layout.order);
      }
      this.applyVisibility([...this.hiddenKeys]);
      const userKeys = new Set(Object.keys(stored?.widths || {}));
      if (useDefaults) {
        this.clearUserWidths();
      } else if (userKeys.size) {
        const userWidths: Record<string, number> = {};
        userKeys.forEach(key => {
          if (layout.widths[key] != null) {
            userWidths[key] = layout.widths[key];
          }
        });
        this.applyWidths(this.clampToHeaderMins(userWidths), userKeys);
      }
      if (notify || (!this.didInitialNotify && (userKeys.size || this.hiddenKeys.size))) {
        this.didInitialNotify = true;
        this.notify();
      }
    } finally {
      this.applying = false;
    }
  }

  private applyDefaultOrder(): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow || this.isGroupedHeader()) {
      return;
    }
    this.stampKeys(headerRow);
    const keyed = Array.from(headerRow.children) as HTMLElement[];
    const order = keyed
      .map(cell => ({
        key: cell.getAttribute(KEY_ATTR) || '',
        index: parseInt(cell.getAttribute(DEFAULT_INDEX_ATTR) || '0', 10),
      }))
      .filter(item => item.key)
      .sort((a, b) => a.index - b.index)
      .map(item => item.key);
    this.applyOrder(order);
  }

  private applyOrder(order: string[]): void {
    if (this.isGroupedHeader()) {
      return;
    }
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    this.stampBodyKeys();
    const rows = this.dataRows();
    const templateOrder = this.templateOrder(headerRow);
    if (!this.canReorderTogether(headerRow, rows, order)) {
      if (templateOrder.length) {
        rows
          .filter(row => row.closest('.ant-table-thead'))
          .forEach(row => this.reorderRow(row, templateOrder));
      }
      this.syncColgroup();
      return;
    }
    rows.forEach(row => this.reorderRow(row, order));
    this.syncColgroup();
  }

  /** Header and body stay aligned: either every row can take `order`, or the header stays on the template. */
  private canReorderTogether(
    headerRow: HTMLTableRowElement,
    rows: HTMLTableRowElement[],
    order: string[],
  ): boolean {
    const count = headerRow.children.length;
    const body = this.contentBodyRows();
    if (body.some(row => row.children.length !== count)) {
      return false;
    }
    if (!rows.length || rows.some(row => !this.rowKeyedForOrder(row, order))) {
      return false;
    }
    return true;
  }

  private contentBodyRows(): HTMLTableRowElement[] {
    return Array.from(this.el.nativeElement.querySelectorAll<HTMLTableRowElement>('.ant-table-tbody tr'))
      .filter(row => {
        if (row.classList.contains('ant-table-measure-now') || row.classList.contains('ant-table-placeholder')) {
          return false;
        }
        const first = row.children[0] as HTMLElement | undefined;
        if (first && Number(first.getAttribute('colspan') || 0) > 1) {
          return false;
        }
        return row.children.length > 0;
      });
  }

  private templateOrder(headerRow: HTMLTableRowElement): string[] {
    return (Array.from(headerRow.children) as HTMLElement[])
      .map(cell => ({
        key: cell.getAttribute(KEY_ATTR) || '',
        index: parseInt(cell.getAttribute(DEFAULT_INDEX_ATTR) || '', 10),
      }))
      .filter(item => item.key && Number.isInteger(item.index))
      .sort((a, b) => a.index - b.index)
      .map(item => item.key);
  }

  private rowKeyedForOrder(row: HTMLElement, order: string[]): boolean {
    const cells = Array.from(row.children) as HTMLElement[];
    if (cells.length !== order.length) {
      return false;
    }
    const keys = cells.map(cell => cell.getAttribute(KEY_ATTR) || '');
    if (keys.some(key => !key) || new Set(keys).size !== keys.length) {
      return false;
    }
    const present = new Set(keys);
    return order.every(key => present.has(key));
  }

  private dataRows(): HTMLTableRowElement[] {
    const headerRow = this.leafHeaderRow();
    const count = headerRow?.children.length ?? 0;
    if (!count) {
      return [];
    }
    return Array.from(
      this.el.nativeElement.querySelectorAll<HTMLTableRowElement>('.ant-table-thead tr, .ant-table-tbody tr'),
    ).filter(row => row.children.length === count && !row.classList.contains('ant-table-measure-now'));
  }

  private reorderRow(row: HTMLElement, order: string[]): boolean {
    const cells = Array.from(row.children) as HTMLElement[];
    if (!this.rowKeyedForOrder(row, order)) {
      return false;
    }
    const current = cells.map(cell => cell.getAttribute(KEY_ATTR) || '');
    if (current.every((key, index) => key === order[index])) {
      return true;
    }
    const byKey = new Map<string, HTMLElement[]>();
    cells.forEach(cell => {
      const key = cell.getAttribute(KEY_ATTR) || '';
      const list = byKey.get(key) || [];
      list.push(cell);
      byKey.set(key, list);
    });
    const next: HTMLElement[] = [];
    for (const key of order) {
      const cell = byKey.get(key)?.shift();
      if (!cell) {
        return false;
      }
      next.push(cell);
    }
    next.forEach(cell => row.appendChild(cell));
    return true;
  }

  private applyWidths(widths: Record<string, number>, userKeys: Set<string>, persistKeys?: Set<string>): void {
    const markUser = persistKeys ?? userKeys;
    this.el.nativeElement.querySelectorAll<HTMLElement>(`th[${KEY_ATTR}], td[${KEY_ATTR}]`).forEach(cell => {
      const key = cell.getAttribute(KEY_ATTR);
      if (!key || widths[key] == null || !userKeys.has(key) || this.hiddenKeys.has(key)) {
        return;
      }
      if (cell.closest('tr.ant-table-measure-now')) {
        return;
      }
      const px = `${Math.round(widths[key])}px`;
      cell.style.width = px;
      cell.style.minWidth = px;
      cell.style.maxWidth = px;
      if (cell.tagName === 'TH' && !this.isFixed(cell) && markUser.has(key)) {
        cell.setAttribute('nzwidth', px);
        cell.setAttribute(COLUMN_USER_WIDTH_ATTR, String(Math.round(widths[key])));
      }
    });
    this.syncColgroup(this.withFixedWidths(widths));
    this.syncTableWidth(this.withFixedWidths(widths));
  }

  private clampToHeaderMins(widths: Record<string, number>): Record<string, number> {
    const headerRow = this.leafHeaderRow();
    const next = { ...widths };
    if (!headerRow) {
      return next;
    }
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR);
      if (!key || next[key] == null || this.isFixed(th)) {
        return;
      }
      next[key] = Math.max(measureColumnHeaderMinWidth(th), next[key]);
    });
    return next;
  }

  private withFixedWidths(widths: Record<string, number>): Record<string, number> {
    const headerRow = this.leafHeaderRow();
    const next = { ...widths };
    if (!headerRow) {
      return next;
    }
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR);
      if (!key || !this.isFixed(th) || next[key] != null) {
        return;
      }
      const declared = parseFloat(th.getAttribute('nzwidth') || '');
      next[key] = Number.isFinite(declared) && declared > 0
        ? declared
        : Math.round(th.getBoundingClientRect().width || COLUMN_MIN_PX);
    });
    return next;
  }

  private pinnedWidths(): Record<string, number> {
    return this.visualWidths();
  }

  private visualWidths(): Record<string, number> {
    const headerRow = this.leafHeaderRow();
    const widths: Record<string, number> = {};
    if (!headerRow) {
      return widths;
    }
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR);
      if (!key || this.hiddenKeys.has(key)) {
        return;
      }
      const userWidth = parseFloat(th.getAttribute(COLUMN_USER_WIDTH_ATTR) || '');
      if (Number.isFinite(userWidth) && userWidth > 0) {
        widths[key] = Math.max(COLUMN_MIN_PX, Math.round(userWidth));
        return;
      }
      const thW = th.getBoundingClientRect().width;
      const td = this.el.nativeElement.querySelector(
        `td[${KEY_ATTR}="${CSS.escape(key)}"]:not(.${COLUMN_HIDDEN_CLASS})`,
      ) as HTMLElement | null;
      const tdW = td && !td.closest('tr.ant-table-measure-now') ? td.getBoundingClientRect().width : 0;
      widths[key] = Math.max(COLUMN_MIN_PX, Math.round(thW || tdW || COLUMN_MIN_PX));
    });
    return widths;
  }

  private syncColgroup(pinned?: Record<string, number>): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    const keys = this.currentKeys(headerRow);
    const widths = pinned && Object.keys(pinned).length ? pinned : this.attrWidths();
    this.el.nativeElement.querySelectorAll('table').forEach(table => {
      const cols = table.querySelectorAll<HTMLElement>('col');
      keys.forEach((key, index) => {
        if (!cols[index]) {
          return;
        }
        if (this.hiddenKeys.has(key)) {
          cols[index].style.width = '0px';
          cols[index].style.minWidth = '0px';
          cols[index].style.maxWidth = '0px';
          return;
        }
        if (widths[key] == null) {
          return;
        }
        const px = `${Math.round(widths[key])}px`;
        cols[index].style.width = px;
        cols[index].style.minWidth = px;
      });
    });
  }

  private attrWidths(): Record<string, number> {
    const headerRow = this.leafHeaderRow();
    const widths: Record<string, number> = {};
    if (!headerRow) {
      return widths;
    }
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR);
      const userWidth = parseFloat(th.getAttribute(COLUMN_USER_WIDTH_ATTR) || '');
      if (key && Number.isFinite(userWidth) && userWidth > 0) {
        widths[key] = userWidth;
      }
    });
    return widths;
  }

  private syncTableWidth(pinned?: Record<string, number>): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    const widths = pinned && Object.keys(pinned).length ? pinned : this.attrWidths();
    const total = this.currentKeys(headerRow)
      .filter(key => !this.hiddenKeys.has(key))
      .reduce((sum, key) => sum + (widths[key] || 0), 0);
    if (total <= 0) {
      return;
    }
    const px = `${Math.round(total)}px`;
    this.el.nativeElement.querySelectorAll('table').forEach(table => {
      (table as HTMLElement).style.width = px;
      (table as HTMLElement).style.minWidth = px;
    });
  }

  private clearUserWidths(): void {
    this.el.nativeElement.querySelectorAll<HTMLElement>(`th[${COLUMN_USER_WIDTH_ATTR}], td[${KEY_ATTR}]`).forEach(cell => {
      if (this.isFixed(cell)) {
        return;
      }
      const fallback = cell.getAttribute(DEFAULT_WIDTH_ATTR);
      cell.style.width = '';
      cell.style.minWidth = '';
      cell.style.maxWidth = '';
      cell.removeAttribute(COLUMN_USER_WIDTH_ATTR);
      if (cell.tagName === 'TH') {
        if (fallback) {
          cell.setAttribute('nzwidth', `${fallback}px`);
        }
      }
    });
    this.el.nativeElement.querySelectorAll<HTMLElement>('col').forEach(col => {
      col.style.width = '';
      col.style.minWidth = '';
    });
    this.el.nativeElement.querySelectorAll('table').forEach(table => {
      (table as HTMLElement).style.width = '';
      (table as HTMLElement).style.minWidth = '';
    });
  }

  private persist(): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow || !this.settingKey) {
      return;
    }
    const keys = this.currentKeys(headerRow);
    const widths: Record<string, number> = {};
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      const key = th.getAttribute(KEY_ATTR);
      if (!key || this.isFixed(th)) {
        return;
      }
      const userWidth = parseFloat(th.getAttribute(COLUMN_USER_WIDTH_ATTR) || '');
      if (Number.isFinite(userWidth) && userWidth > 0) {
        widths[key] = userWidth;
      }
    });
    const layout: IColumnLayout = { order: keys, widths, hidden: [...this.hiddenKeys] };
    this.userSettings.set(this.settingKey, layout);
    this.notify();
    this.reapplyAfterRender();
  }

  private reapplyAfterRender(): void {
    this.scheduleSetup();
  }

  private notify(): void {
    this.el.nativeElement.dispatchEvent(new CustomEvent(COLUMN_LAYOUT_EVENT, { bubbles: true }));
  }

  private attachResizeHandles(): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return;
    }
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      if (this.isFixed(th) || th.querySelector(`.${HANDLE_CLASS}`)) {
        return;
      }
      const handle = document.createElement('span');
      handle.className = HANDLE_CLASS;
      handle.setAttribute('aria-hidden', 'true');
      th.classList.add('col-layout-resizable');
      th.appendChild(handle);
      this.zone.runOutsideAngular(() => {
        handle.addEventListener('pointerdown', event => this.onResizeStart(event, th));
        handle.addEventListener('dblclick', event => {
          event.preventDefault();
          event.stopPropagation();
          const key = th.getAttribute(KEY_ATTR);
          if (key) {
            this.zone.run(() => this.autosizeColumn(key));
          }
        });
      });
    });
  }

  private onResizeStart(event: PointerEvent, th: HTMLElement): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const key = th.getAttribute(KEY_ATTR);
    if (!key) {
      return;
    }
    this.resizing = true;
    const startX = event.clientX;
    const pinned = this.pinnedWidths();
    const minWidth = measureColumnHeaderMinWidth(th);
    const startWidth = Math.max(minWidth, pinned[key] || th.getBoundingClientRect().width);
    const userKeys = new Set(
      Object.keys(pinned).filter(item => {
        const cell = this.leafHeaderRow()?.querySelector(`[${KEY_ATTR}="${CSS.escape(item)}"]`) as HTMLElement | null;
        return !!cell && !this.isFixed(cell);
      }),
    );
    if ((pinned[key] || 0) < minWidth) {
      this.applyWidths({ ...pinned, [key]: minWidth }, userKeys);
    }
    const move = (moveEvent: PointerEvent) => {
      const next = Math.max(minWidth, Math.round(startWidth + (moveEvent.clientX - startX)));
      this.applyWidths({ ...pinned, [key]: next }, userKeys);
      this.syncHeaderTooltips();
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      this.resizing = false;
      this.zone.run(() => this.persist());
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  private attachReorder(headerRow: HTMLTableRowElement): void {
    this.headerPointerUnlisten?.();
    this.headerPointerUnlisten = null;
    if (this.isGroupedHeader()) {
      return;
    }
    const onDown = (event: PointerEvent) => this.onReorderPointerDown(event, headerRow);
    const onClick = (event: MouseEvent) => {
      if (this.allowHeaderClick) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      if (target.closest(HEADER_CONTROL_SELECTOR)) {
        return;
      }
      if (target.closest('th.col-layout-movable')) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    this.zone.runOutsideAngular(() => {
      headerRow.addEventListener('pointerdown', onDown);
      headerRow.addEventListener('click', onClick, true);
      this.headerPointerUnlisten = () => {
        headerRow.removeEventListener('pointerdown', onDown);
        headerRow.removeEventListener('click', onClick, true);
      };
    });
    Array.from(headerRow.children).forEach(cell => {
      const th = cell as HTMLElement;
      if (!this.isFixed(th)) {
        th.classList.add('col-layout-movable');
      }
    });
  }

  private onReorderPointerDown(event: PointerEvent, headerRow: HTMLTableRowElement): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (target.closest(`.${HANDLE_CLASS}, ${HEADER_CONTROL_SELECTOR}`)) {
      return;
    }
    const th = target.closest('th') as HTMLElement | null;
    if (!th || this.isFixed(th) || !headerRow.contains(th)) {
      return;
    }
    const draggedKey = th.getAttribute(KEY_ATTR);
    if (!draggedKey) {
      return;
    }
    const startX = event.clientX;
    let dragging = false;
    let ghost: HTMLElement | null = null;
    let dropTh: HTMLElement | null = null;
    let dropAfter = false;

    const clearDrop = () => {
      headerRow.querySelectorAll(`.${DROP_BEFORE_CLASS}, .${DROP_AFTER_CLASS}`).forEach(cell => {
        cell.classList.remove(DROP_BEFORE_CLASS, DROP_AFTER_CLASS);
      });
    };

    const move = (moveEvent: PointerEvent) => {
      if (!dragging) {
        if (Math.abs(moveEvent.clientX - startX) < DRAG_THRESHOLD_PX) {
          return;
        }
        dragging = true;
        this.reordering = true;
        th.classList.add('col-layout-source');
        ghost = th.cloneNode(true) as HTMLElement;
        ghost.classList.add('col-layout-ghost');
        ghost.style.width = `${th.getBoundingClientRect().width}px`;
        ghost.style.height = `${th.getBoundingClientRect().height}px`;
        document.body.appendChild(ghost);
      }
      if (ghost) {
        ghost.style.left = `${moveEvent.clientX + 12}px`;
        ghost.style.top = `${moveEvent.clientY - 16}px`;
      }
      const under = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY);
      const overTh = under?.closest('th') as HTMLElement | null;
      clearDrop();
      if (!overTh || overTh === th || this.isFixed(overTh) || !headerRow.contains(overTh)) {
        dropTh = null;
        return;
      }
      const rect = overTh.getBoundingClientRect();
      dropAfter = moveEvent.clientX > rect.left + rect.width / 2;
      dropTh = overTh;
      overTh.classList.add(dropAfter ? DROP_AFTER_CLASS : DROP_BEFORE_CLASS);
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      clearDrop();
      ghost?.remove();
      th.classList.remove('col-layout-source');
      this.reordering = false;
      if (!dragging || !dropTh) {
        return;
      }
      const targetKey = dropTh.getAttribute(KEY_ATTR);
      if (!targetKey) {
        return;
      }
      const next = placeColumnKey(this.currentKeys(headerRow), draggedKey, targetKey, dropAfter);
      this.applying = true;
      try {
        this.applyOrder(next);
      } finally {
        this.applying = false;
      }
      const eatClick = (clickEvent: Event) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };
      th.addEventListener('click', eatClick, true);
      setTimeout(() => th.removeEventListener('click', eatClick, true), 0);
      this.zone.run(() => this.persist());
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  private attachColumnMenu(headerRow: HTMLTableRowElement): void {
    this.menuUnlisten?.();
    const onContext = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.closest(`.${HANDLE_CLASS}`)) {
        return;
      }
      const th = target.closest('th') as HTMLElement | null;
      if (!th || !headerRow.contains(th) || this.isFixed(th)) {
        return;
      }
      const key = th.getAttribute(KEY_ATTR);
      if (!key || this.hiddenKeys.has(key)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.openColumnMenu(th, key, event.clientX, event.clientY);
    };
    this.zone.runOutsideAngular(() => {
      headerRow.addEventListener('contextmenu', onContext);
      this.menuUnlisten = () => headerRow.removeEventListener('contextmenu', onContext);
    });
  }

  private openColumnMenu(th: HTMLElement, key: string, x: number, y: number): void {
    this.closeColumnMenu();
    const sortable = !!th.querySelector('.ant-table-column-sorter');
    const filterable = !!th.querySelector('.ant-table-filter-trigger, nz-filter-trigger');
    const hideable = this.canHideColumn(key);
    const menu = document.createElement('div');
    menu.className = MENU_CLASS;
    menu.setAttribute('role', 'menu');
    if (sortable) {
      this.appendMenuButton(menu, 'Aufsteigend sortieren', () => this.triggerSort(th, 'ascend'));
      this.appendMenuButton(menu, 'Absteigend sortieren', () => this.triggerSort(th, 'descend'));
      this.appendMenuButton(menu, 'Sortierung aufheben', () => this.triggerSort(th, null));
    }
    if (filterable) {
      this.appendMenuButton(menu, 'Filtern', () => this.triggerFilter(th));
    }
    if (sortable || filterable) {
      const sep = document.createElement('div');
      sep.className = `${MENU_CLASS}__sep`;
      sep.setAttribute('role', 'separator');
      menu.appendChild(sep);
    }
    this.appendMenuButton(menu, 'An Inhalt anpassen', () => this.autosizeColumn(key));
    this.appendMenuButton(menu, 'Spalte ausblenden', () => this.setColumnVisible(key, false), !hideable);
    document.body.appendChild(menu);
    const width = menu.offsetWidth;
    const height = menu.offsetHeight;
    menu.style.left = `${Math.max(8, Math.min(x, window.innerWidth - width - 8))}px`;
    menu.style.top = `${Math.max(8, Math.min(y, window.innerHeight - height - 8))}px`;
    this.menuEl = menu;
    const onDoc = (event: Event) => {
      if (event.target instanceof Node && menu.contains(event.target)) {
        return;
      }
      this.closeColumnMenu();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.closeColumnMenu();
      }
    };
    window.addEventListener('pointerdown', onDoc, true);
    window.addEventListener('keydown', onKey);
    menu.dataset.unlisten = '1';
    (menu as HTMLElement & { _unlisten?: () => void })._unlisten = () => {
      window.removeEventListener('pointerdown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }

  private appendMenuButton(menu: HTMLElement, label: string, action: () => void, disabled = false): void {
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('role', 'menuitem');
    button.textContent = label;
    button.disabled = disabled;
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.closeColumnMenu();
      this.zone.run(action);
    });
    menu.appendChild(button);
  }

  private closeColumnMenu(): void {
    if (!this.menuEl) {
      return;
    }
    const extra = this.menuEl as HTMLElement & { _unlisten?: () => void };
    extra._unlisten?.();
    this.menuEl.remove();
    this.menuEl = null;
  }

  private canHideColumn(key: string): boolean {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return false;
    }
    const locked = new Set(this.lockedKeys(headerRow));
    if (locked.has(key)) {
      return false;
    }
    const hideable = this.currentKeys(headerRow).filter(item => !locked.has(item) && !this.hiddenKeys.has(item));
    return hideable.length > 1;
  }

  private autosizeColumn(key: string): void {
    const headerRow = this.leafHeaderRow();
    if (!headerRow || !key || this.hiddenKeys.has(key)) {
      return;
    }
    this.stampBodyKeys();
    const th = this.headerCell(key);
    if (!th || this.isFixed(th)) {
      return;
    }
    const content = measureColumnAutosizeWidth(this.el.nativeElement, key);
    const snapshot = this.visualWidths();
    const others = Object.keys(snapshot)
      .filter(item => item !== key)
      .reduce((sum, item) => sum + (snapshot[item] || 0), 0);
    const next = fillColumnWidth(content, others, this.tableViewportWidth());
    snapshot[key] = next;
    const pinKeys = new Set(
      Object.keys(snapshot).filter(item => {
        if (this.hiddenKeys.has(item) || !(snapshot[item] > 0)) {
          return false;
        }
        const cell = this.headerCell(item);
        return !!cell && !this.isFixed(cell);
      }),
    );
    const persistKeys = pinKeys;
    this.applying = true;
    try {
      this.applyWidths({ ...snapshot, [key]: next }, pinKeys, persistKeys);
      this.persist();
    } finally {
      this.applying = false;
    }
  }

  private tableViewportWidth(): number {
    const host = this.el.nativeElement;
    const box = (host.closest('.inner-content') as HTMLElement | null)
      || host.parentElement
      || host;
    const style = getComputedStyle(box);
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    return Math.max(0, Math.round(box.clientWidth - padding));
  }

  private headerCell(key: string): HTMLElement | null {
    const headerRow = this.leafHeaderRow();
    if (!headerRow) {
      return null;
    }
    return headerRow.querySelector(`[${KEY_ATTR}="${CSS.escape(key)}"]`) as HTMLElement | null;
  }

  private findThAddOn(key: string | null): NzThAddOnComponent<unknown> | undefined {
    if (!key) {
      return undefined;
    }
    return this.thead()?.listOfNzThAddOnComponent?.find(item => item.nzColumnKey === key);
  }

  private triggerSort(th: HTMLElement, order: 'ascend' | 'descend' | null): void {
    const addon = this.findThAddOn(th.getAttribute(KEY_ATTR));
    if (addon?.nzShowSort) {
      if (order === null) {
        addon.clearSortOrder();
      } else {
        addon.setSortOrder(order);
      }
      addon.manualClickOrder$.next(addon);
      return;
    }
    this.cycleSortByClick(th, order);
  }

  private cycleSortByClick(th: HTMLElement, order: 'ascend' | 'descend' | null): void {
    if (!th.querySelector('.ant-table-column-sorter')) {
      return;
    }
    const isActive = (which: 'up' | 'down') =>
      !!th.querySelector(`.ant-table-column-sorter-${which}.active, .ant-table-column-sorter-${which}.on`);
    this.allowHeaderClick = true;
    try {
      for (let i = 0; i < 4; i++) {
        const up = isActive('up');
        const down = isActive('down');
        if (order === 'ascend' && up) {
          return;
        }
        if (order === 'descend' && down) {
          return;
        }
        if (order === null && !up && !down) {
          return;
        }
        th.click();
      }
    } finally {
      this.allowHeaderClick = false;
    }
  }

  private triggerFilter(th: HTMLElement): void {
    const trigger = th.querySelector('.ant-table-filter-trigger') as HTMLElement | null;
    if (!trigger) {
      return;
    }
    this.allowHeaderClick = true;
    window.setTimeout(() => {
      try {
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      } finally {
        this.allowHeaderClick = false;
      }
    }, 50);
  }
}
